import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { exerciseBankService, type ExerciseSet } from "@/services/exerciseBankService";
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  FileJson,
  Upload,
  Play,
  FileText
} from "lucide-react";

export default function AdminBanqueExercices() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [jsonInput, setJsonInput] = useState("");
  const [exerciseSets, setExerciseSets] = useState<ExerciseSet[]>([]);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Load existing sets
  const loadSets = () => {
    const sets = exerciseBankService.getExerciseSets();
    setExerciseSets(sets);
  };

  useEffect(() => {
    loadSets();

    const handleUpdate = () => loadSets();
    window.addEventListener("exerciseSetsUpdated", handleUpdate);
    return () => window.removeEventListener("exerciseSetsUpdated", handleUpdate);
  }, []);

  const handleValidateAndImport = () => {
    setStatus(null);
    const cleanedJson = jsonInput.trim();
    
    if (!cleanedJson) {
      setStatus({
        type: "error",
        message: "O campo de texto está vazio. Cole o JSON do exercício antes de validar."
      });
      return;
    }

    try {
      // Step 1: JSON syntax parse
      const parsedSet = exerciseBankService.importExerciseSet(cleanedJson);
      
      setStatus({
        type: "success",
        message: `Exercício "${parsedSet.title}" importado com sucesso! (${parsedSet.questions.length} questões inseridas na categoria ${parsedSet.category}).`
      });
      setJsonInput(""); // Clear textarea on success
      loadSets();
    } catch (err: any) {
      console.error("Import validation error:", err);
      let errorMsg = err.message || "Erro desconhecido ao processar o JSON.";

      // Check for JSON syntax error with position to extract line details
      if (err instanceof SyntaxError) {
        const positionMatch = err.message.match(/at position (\d+)/i) || err.message.match(/char (\d+)/i);
        if (positionMatch) {
          const position = parseInt(positionMatch[1], 10);
          const linesUpToError = cleanedJson.substring(0, position).split("\n");
          const lineNumber = linesUpToError.length;
          const colNumber = linesUpToError[linesUpToError.length - 1].length + 1;
          errorMsg = `Erro de Sintaxe JSON na Linha ${lineNumber}, Coluna ${colNumber}: ${err.message}`;
        } else {
          errorMsg = `Erro de Sintaxe no JSON: ${err.message}`;
        }
      }

      setStatus({
        type: "error",
        message: errorMsg
      });
    }
  };

  const handleDeleteSet = (id: string) => {
    if (confirm("Deseja realmente excluir este conjunto de exercícios?")) {
      exerciseBankService.deleteExerciseSet(id);
      loadSets();
      setStatus({
        type: "success",
        message: "Conjunto de exercícios excluído com sucesso."
      });
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-16 animate-in fade-in duration-300 text-left">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-5 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5 uppercase font-bold text-[10px] tracking-wider px-2.5 py-0.5 rounded-full">
              Painel Admin
            </Badge>
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight leading-none">
            Importar Exercícios (JSON)
          </h1>
          <p className="text-slate-500 text-xs md:text-sm font-semibold mt-1.5">
            Cadastre novos blocos de questões de leitura (CE) ou escuta (CO) via payload estruturado.
          </p>
        </div>

        <Button 
          variant="outline" 
          onClick={() => navigate("/admin")} 
          className="rounded-xl border border-slate-200 text-slate-600 hover:text-slate-900 gap-1.5 h-10 px-4 bg-white shadow-3xs self-start"
        >
          <ArrowLeft className="w-4 h-4 text-slate-400" />
          Voltar ao Painel
        </Button>
      </div>

      <div className="grid gap-8 lg:grid-cols-12 items-start">
        {/* Left Column: Import Textarea */}
        <div className="lg:col-span-7 space-y-6">
          <Card className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs bg-white">
            <CardHeader className="p-6 border-b border-slate-100/50 bg-slate-50/20">
              <CardTitle className="text-base font-extrabold text-slate-800 flex items-center gap-2">
                <FileJson className="w-5 h-5 text-indigo-500" />
                Payload do Exercício
              </CardTitle>
              <CardDescription className="text-xs font-semibold text-slate-500">
                Cole a estrutura JSON do set de exercícios. O validador verificará a integridade do formato.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Textarea
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder={`{\n  "title": "Documentos de la vie quotidienne - Set 01",\n  "category": "CE",\n  "level": "B1",\n  "questions": [\n    {\n      "id": 1,\n      "text": "O que o texto sugere sobre o horário?",\n      "options": ["Abre às 8h", "Abre às 9h"],\n      "correct_index": 1,\n      "explanation": "O texto menciona que abre às 9h."\n    }\n  ]\n}`}
                  className="font-mono text-xs p-4 min-h-[360px] rounded-xl border border-slate-200 focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none leading-relaxed text-slate-800 bg-slate-50/20 resize-y"
                />
              </div>

              {status && (
                <div className={`p-4 rounded-xl border flex items-start gap-3 animate-in fade-in duration-200 text-xs ${
                  status.type === "success" 
                    ? "bg-emerald-50/50 border-emerald-100 text-emerald-800" 
                    : "bg-rose-50/50 border-rose-100 text-rose-800"
                }`}>
                  {status.type === "success" ? (
                    <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  )}
                  <div className="space-y-1">
                    <p className="font-extrabold text-[13px]">
                      {status.type === "success" ? "Sucesso!" : "Erro de Validação"}
                    </p>
                    <p className="font-semibold leading-relaxed whitespace-pre-wrap">{status.message}</p>
                  </div>
                </div>
              )}

              <Button
                onClick={handleValidateAndImport}
                className="w-full h-11 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs px-5 shadow-xs transition-all flex items-center justify-center gap-1.5"
              >
                <Upload className="w-4 h-4" />
                Validar e Importar
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Existing sets catalogue */}
        <div className="lg:col-span-5 space-y-6">
          <Card className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs bg-white">
            <CardHeader className="p-6 border-b border-slate-100/50 bg-slate-50/20">
              <CardTitle className="text-base font-extrabold text-slate-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-500" />
                Exercícios Cadastrados ({exerciseSets.length})
              </CardTitle>
              <CardDescription className="text-xs font-semibold text-slate-500">
                Lista de conjuntos ativos salvos no banco. Você pode gerenciá-los ou testá-los diretamente.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {exerciseSets.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileJson className="w-10 h-10 mx-auto stroke-[1.5] mb-2 text-slate-300" />
                  <p className="font-bold text-xs">Nenhum exercício cadastrado</p>
                  <p className="text-[10px] font-semibold text-slate-400 mt-1 max-w-[200px] mx-auto">
                    Cole o JSON à esquerda para preencher o banco local de questões.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                  {exerciseSets.map((set) => (
                    <div 
                      key={set.id} 
                      className="p-4 rounded-xl border border-slate-100 hover:border-slate-200/80 bg-white transition-all duration-200 flex items-center justify-between gap-4 shadow-3xs"
                    >
                      <div className="space-y-1.5 min-w-0">
                        <h5 className="font-extrabold text-slate-900 text-xs md:text-sm truncate">
                          {set.title}
                        </h5>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge className="bg-slate-100 hover:bg-slate-100 text-slate-600 border-none font-bold text-[8px] tracking-wide uppercase px-2 py-0.5 rounded shadow-3xs">
                            {set.category}
                          </Badge>
                          <Badge className="bg-primary/5 hover:bg-primary/5 text-primary border-none font-bold text-[8px] tracking-wide uppercase px-2 py-0.5 rounded shadow-3xs">
                            {set.level}
                          </Badge>
                          <span className="text-[9px] font-semibold text-slate-400">
                            · {set.questions.length} questões
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/reading` /* redirect to exercise list catalog */)}
                          className="h-8 w-8 rounded-lg text-slate-400 hover:text-primary hover:bg-slate-50 transition-colors"
                          title="Visualizar no catálogo"
                        >
                          <Play className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteSet(set.id)}
                          className="h-8 w-8 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
